import { Box, Button, Divider, Group, NumberInput, Paper, SimpleGrid, Skeleton, Stack, Switch, Text, Title, type NumberInputProps } from '@mantine/core';
import { isInRange, useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { Feature, MfaFactor, OfflineResume, type Policy } from '@motionsafe/ms-console-api/common/v1/common_pb.js';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useId, type ChangeEvent, type ReactNode } from 'react';

import { api } from '../../api/client';
import { errorText, legalDocumentsQuery, orgAgreementQuery, orgQuery, useInvalidate } from '../../api/hooks';
import { LOCKED_INPUT, LOCKED_ORDER, locked, lockedNote, LockedSwitch, lockedSwitch } from '../../components/locked';
import { PageHeader } from '../../components/page';
import { SignInTimeline } from '../../components/sign-in-timeline';
import { isStaff, STAFF_ORG_CAPTION, STAFF_ORG_SLUG } from '../../lib/roles';
import { Route as AppRoute, useAccess, useOrgId } from '../_app';

export const Route = createFileRoute('/_app/policy')({ component: PolicyPage });

const TITLE = 'Policy';
const PURPOSE = 'Sessions, 2FA, locking, and sharing for this organization.';

function PolicyPage() {
  const orgId = useOrgId();
  const org = useQuery(orgQuery(orgId));
  if (org.isError) {
    return (
      <>
        <PageHeader title={TITLE} description={PURPOSE} />
        <Text c="danger">{errorText(org.error)}</Text>
      </>
    );
  }
  if (!org.data?.policy) {
    return (
      <>
        <PageHeader title={TITLE} description={PURPOSE} />
        {/* The form's own shape: two rows of paired sections, then the wide one. */}
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" aria-busy="true">
          {[320, 320, 280].map((h, i) => <Skeleton key={i} height={h} radius="md" />)}
        </SimpleGrid>
      </>
    );
  }
  return <PolicyForm key={orgId} orgId={orgId} slug={org.data.slug} initial={toValues(org.data.policy, org.data.features)} />;
}

// One card of the form. Its title sits in a band of its own, ruled off from
// what it holds, so a heading is never mistaken for a setting; the line
// under the title says what the card governs. The settings follow, parted
// by hairlines with room around each, since a policy is read more often
// than it is changed.
function Section({ title, description, wide, children }: { title: string; description: string; wide?: boolean | undefined; children: ReactNode }) {
  const id = useId();
  return (
    <Paper component="section" p={0} aria-labelledby={id} style={{ overflow: 'hidden', ...(wide ? { gridColumn: '1 / -1' } : {}) }}>
      <Box component="header" px={{ base: 'lg', md: 'xl' }} py={14} style={{ borderBottom: '1px solid var(--mantine-color-default-border)', background: 'var(--mantine-color-default-hover)' }}>
        <Title order={2} fz="md" lh={1.3} id={id}>{title}</Title>
        <Text fz={13} lh={1.45} c="dimmed" mt={2}>{description}</Text>
      </Box>
      {/* The body lines up with the head on the same inset, and sits a step closer to it than the settings sit to each other. */}
      <Stack gap="lg" px={{ base: 'lg', md: 'xl' }} py="lg" className="settings">{children}</Stack>
    </Paper>
  );
}

// A count with its unit beside it: the input stays narrow and tabular. A
// cleared field holds '' until a number is entered, never 0, so the range
// rule catches it; the value is not clamped on blur, so an out-of-range entry
// stays visible with the sentence that says what fits. The unit is part of
// the input's name. A locked count reads as a value first, with what it does
// and who can change it underneath (DESIGN.web.md 7.6), as a locked switch
// does.
function Amount({ label, unit, locked, ...props }: NumberInputProps & { label: string; unit?: string; locked?: boolean }) {
  return (
    <NumberInput
      label={label}
      clampBehavior="none"
      allowDecimal={false}
      allowNegative={false}
      classNames={{ input: 'num' }}
      descriptionProps={{ maw: '64ch' }}
      inputContainer={(input) => (
        <Group gap="sm" wrap="nowrap" align="center">
          <Box w={88}>{input}</Box>
          {unit && <Text size="sm" c="dimmed">{unit}</Text>}
        </Group>
      )}
      {...(unit ? { 'aria-label': `${label} in ${unit}` } : {})}
      {...(locked ? { ...LOCKED_INPUT, inputWrapperOrder: LOCKED_ORDER } : {})}
      {...props}
    />
  );
}

// How far a switch's text sits from its edge at the default size: the track, then the label's padding.
const SWITCH_INDENT = 46;

// The ranges a save accepts, each refusal a sentence naming what fits.
const LIMITS = {
  sessionTtlHours: { min: 1, max: 336 },
  offlineGraceHours: { min: 0, max: 72 },
  maxCachedSessions: { min: 1, max: 5 },
  maxSignInDays: { min: 1, max: 30 },
  idleLockMinutes: { min: 1, max: 1440 },
} as const;

// What the form edits. A count is a number, or '' while its field is cleared.
type Count = number | string;
interface PolicyValues {
  sessionTtlHours: Count;
  offlineGraceHours: Count;
  requireMfa: boolean;
  mfaLocked: boolean;
  offlineResume: OfflineResume;
  maxCachedSessions: Count;
  maxSignInDays: Count;
  idleLock: boolean;
  idleLockMinutes: Count;
  decryptedSharing: boolean;
  wifiDirectSend: boolean;
  wifiDirectReceive: boolean;
  crossOrgSend: boolean;
  crossOrgReceive: boolean;
  // MotionSafe's sharing overrides, which staff set here and SetFeature saves.
  overrides: Record<Override, boolean>;
}

// The sharing overrides, each an `org_features` row: on is allowed.
type Override = 'decryptedSharing' | 'wifiDirect' | 'crossOrg';
const OVERRIDE_FEATURE: Record<Override, Feature> = { decryptedSharing: Feature.DECRYPTED_SHARING, wifiDirect: Feature.WIFI_DIRECT_SHARING, crossOrg: Feature.CROSS_ORG_TRANSFER };
const OVERRIDE_NAMES = Object.keys(OVERRIDE_FEATURE) as Override[];

function toValues(p: Policy, features: Feature[]): PolicyValues {
  return {
    sessionTtlHours: p.sessionTtlHours,
    offlineGraceHours: p.offlineGraceHours,
    requireMfa: p.requireMfa,
    mfaLocked: p.mfaLocked,
    offlineResume: p.offlineResume,
    maxCachedSessions: p.maxCachedSessions,
    maxSignInDays: p.maxSignInDays || 7,
    // Off is 0 on the wire; the form keeps the minutes to come back to.
    idleLock: (p.idleLockMinutes ?? 15) > 0,
    idleLockMinutes: p.idleLockMinutes || 15,
    // Absent on an older policy is on.
    decryptedSharing: p.decryptedSharing ?? true,
    wifiDirectSend: p.wifiDirectSend ?? true,
    wifiDirectReceive: p.wifiDirectReceive ?? true,
    crossOrgSend: p.crossOrgSend ?? false,
    crossOrgReceive: p.crossOrgReceive ?? false,
    // The service lists the overrides that allow, defaults included.
    overrides: { decryptedSharing: features.includes(Feature.DECRYPTED_SHARING), wifiDirect: features.includes(Feature.WIFI_DIRECT_SHARING), crossOrg: features.includes(Feature.CROSS_ORG_TRANSFER) },
  };
}

// Only a valid form is saved, so every count is a number by then.
const count = (c: Count) => (typeof c === 'number' ? c : Number.NaN);

function toPolicy(v: PolicyValues) {
  return {
    sessionTtlHours: count(v.sessionTtlHours),
    offlineGraceHours: count(v.offlineGraceHours),
    requireMfa: v.requireMfa,
    mfaLocked: v.mfaLocked,
    // The one factor there is; the service stores it whatever is sent.
    allowedFactors: [MfaFactor.TOTP],
    offlineResume: v.offlineResume,
    // Greyed out without PIN locking: 0 keeps what is stored.
    maxCachedSessions: typeof v.maxCachedSessions === 'number' ? v.maxCachedSessions : 0,
    // These two are greyed out without PIN locking, so may not be numbers
    // then: 0 days keeps what is stored, and no minutes leaves them as they are.
    maxSignInDays: typeof v.maxSignInDays === 'number' ? v.maxSignInDays : 0,
    idleLockMinutes: !v.idleLock ? 0 : typeof v.idleLockMinutes === 'number' ? v.idleLockMinutes : undefined,
    decryptedSharing: v.decryptedSharing,
    wifiDirectSend: v.wifiDirectSend,
    wifiDirectReceive: v.wifiDirectReceive,
    crossOrgSend: v.crossOrgSend,
    crossOrgReceive: v.crossOrgReceive,
  };
}

// The overrides a save changes.
const changedOverrides = (a: PolicyValues, b: PolicyValues) => OVERRIDE_NAMES.filter((n) => a.overrides[n] !== b.overrides[n]);

// The fields a save sends, compared one by one.
function sameValues(a: PolicyValues, b: PolicyValues): boolean {
  return samePolicy(a, b) && changedOverrides(a, b).length === 0;
}

// The policy's own fields, which SetPolicy saves.
function samePolicy(a: PolicyValues, b: PolicyValues): boolean {
  return a.sessionTtlHours === b.sessionTtlHours
    && a.offlineGraceHours === b.offlineGraceHours
    && a.requireMfa === b.requireMfa
    && a.mfaLocked === b.mfaLocked
    && a.offlineResume === b.offlineResume
    && a.maxCachedSessions === b.maxCachedSessions
    && a.maxSignInDays === b.maxSignInDays
    && a.idleLock === b.idleLock
    && (!a.idleLock || a.idleLockMinutes === b.idleLockMinutes)
    && a.decryptedSharing === b.decryptedSharing
    && a.wifiDirectSend === b.wifiDirectSend
    && a.wifiDirectReceive === b.wifiDirectReceive
    && a.crossOrgSend === b.crossOrgSend
    && a.crossOrgReceive === b.crossOrgReceive;
}

type SharingField = 'decryptedSharing' | 'wifiDirectSend' | 'wifiDirectReceive' | 'crossOrgSend' | 'crossOrgReceive';

// Decrypted sharing and Wi-Fi Direct are allowed unless MotionSafe turns them off; cross-organization transfer is off until it allows it.
const TURNED_OFF = { theirs: 'Turned off for your organization by MotionSafe. Your MotionSafe account manager can turn it back on.', staff: 'Off whatever this says while the override above does not allow it.' };
const NOT_ALLOWED = { theirs: 'Not allowed for your organization by MotionSafe. Your MotionSafe account manager can allow it.', staff: 'Off whatever this says while the override above does not allow it.' };

// Each sharing override as staff see it, above the settings it holds.
const OVERRIDE: Record<Override, { label: string; hint: string }> = {
  decryptedSharing: { label: 'MotionSafe allows decrypted sharing', hint: 'Sharing override, staff only. Allowed unless turned off. Off, nobody in this organization shares decrypted, whatever its administrators choose below.' },
  wifiDirect: { label: 'MotionSafe allows Wi-Fi Direct', hint: 'Sharing override, staff only. Allowed unless turned off. Off, this organization\'s devices neither send nor receive over Wi-Fi Direct, whatever its administrators choose below.' },
  crossOrg: { label: 'MotionSafe allows transfers between organizations', hint: 'Sharing override, staff only. Off until allowed. Allowed, its administrators choose below whether to send, receive, or both.' },
};

// What each sharing setting does, the override that holds it, and what is said while that override does not allow it.
const SHARING: Record<SharingField, { label: string; hint: string; override: Override; missing: { theirs: string; staff: string } }> = {
  decryptedSharing: {
    label: 'Allow decrypted sharing',
    hint: 'Lets operators share a collection decrypted over USB. Each one asks for a justification, recorded in the Sharing audit. Shares that stay encrypted are recorded there too.',
    override: 'decryptedSharing',
    missing: TURNED_OFF,
  },
  wifiDirectSend: {
    label: 'Send over Wi-Fi Direct',
    hint: 'Lets operators send a collection from one of this organization\'s devices to another device over Wi-Fi Direct. It stays encrypted, and each send is recorded in the Sharing audit.',
    override: 'wifiDirect',
    missing: TURNED_OFF,
  },
  wifiDirectReceive: {
    label: 'Receive over Wi-Fi Direct',
    hint: 'Lets this organization\'s devices receive a collection sent from another device over Wi-Fi Direct. Each one is recorded in the Sharing audit.',
    override: 'wifiDirect',
    missing: TURNED_OFF,
  },
  crossOrgSend: {
    label: 'Send to other organizations',
    hint: 'Lets operators send a collection over Wi-Fi Direct to a device of another organization that allows receiving it. Each send asks for a justification, and both organizations are told. Needs Send over Wi-Fi Direct.',
    override: 'crossOrg',
    missing: NOT_ALLOWED,
  },
  crossOrgReceive: {
    label: 'Receive from other organizations',
    hint: 'Lets this organization\'s devices receive a collection from a device of another organization that allows sending it. The copy opens with this organization\'s own key, and both organizations are told. Needs Receive over Wi-Fi Direct.',
    override: 'crossOrg',
    missing: NOT_ALLOWED,
  },
};

function PolicyForm({ orgId, slug, initial }: { orgId: string; slug: string; /** The saved policy and overrides. */ initial: PolicyValues }) {
  const { me } = AppRoute.useRouteContext();
  const staff = isStaff(me);
  // An organization's policy is an admin's to change on its behalf: other
  // staff see the policy locked, every value in place, and the caption
  // beside Save. The sharing overrides are Accounts' as well, but for the
  // MotionSafe organization, which is an admin's alone.
  const access = useAccess();
  const write = access.may('SetPolicy');
  const ours = slug === STAFF_ORG_SLUG && !access.unrestricted;
  const writeOverrides = staff && !ours && access.may('SetFeature');
  const overrideCaption = ours ? STAFF_ORG_CAPTION : access.caption('SetFeature');
  const saveCaption = useId();
  const inv = useInvalidate();
  // The form's initial values are the saved policy; each refusal shows as
  // soon as the field changes.
  const form = useForm<PolicyValues>({
    initialValues: initial,
    validateInputOnChange: true,
    // Two rules read other fields: check them again when those change, so a
    // refusal never sits unseen behind a disabled Save.
    onValuesChange: (next, previous) => {
      if (next.sessionTtlHours !== previous.sessionTtlHours || next.offlineResume !== previous.offlineResume || next.idleLock !== previous.idleLock) {
        void form.validateField('maxSignInDays');
        void form.validateField('idleLockMinutes');
        void form.validateField('maxCachedSessions');
      }
    },
    validate: {
      sessionTtlHours: isInRange(LIMITS.sessionTtlHours, 'Enter a whole number of hours from 1 to 336.'),
      offlineGraceHours: isInRange(LIMITS.offlineGraceHours, 'Enter a whole number of hours from 0 to 72.'),
      maxCachedSessions: (value, values) => (values.offlineResume !== OfflineResume.PIN || (typeof value === 'number' && Number.isInteger(value) && value >= LIMITS.maxCachedSessions.min && value <= LIMITS.maxCachedSessions.max)
        ? null
        : 'Enter a whole number from 1 to 5.'),
      // Never shorter than one session, so the PIN is asked for before the
      // password is. Without PIN locking the field is greyed out, and the
      // service holds it to the session length itself.
      maxSignInDays: (value, values) => {
        if (values.offlineResume !== OfflineResume.PIN) return null;
        const days = typeof value === 'number' ? value : Number.NaN;
        if (!Number.isInteger(days) || days < LIMITS.maxSignInDays.min || days > LIMITS.maxSignInDays.max) return 'Enter a whole number of days from 1 to 30.';
        const sessionHours = typeof values.sessionTtlHours === 'number' ? values.sessionTtlHours : 0;
        return days * 24 < sessionHours ? `Make it at least ${Math.ceil(sessionHours / 24)} days, the session length.` : null;
      },
      // Never longer than a session, where the PIN is asked for anyway. Off, the minutes are not saved.
      idleLockMinutes: (value, values) => {
        if (!values.idleLock || values.offlineResume !== OfflineResume.PIN) return null;
        const minutes = typeof value === 'number' ? value : Number.NaN;
        if (!Number.isInteger(minutes) || minutes < LIMITS.idleLockMinutes.min || minutes > LIMITS.idleLockMinutes.max) return 'Enter a whole number of minutes from 1 to 1440, a day.';
        const sessionHours = typeof values.sessionTtlHours === 'number' ? values.sessionTtlHours : Infinity;
        return minutes > sessionHours * 60 ? `Make it at most ${sessionHours * 60} minutes, the session length.` : null;
      },
    },
  });
  const save = useMutation({
    // The overrides first, then the policy, each only when it changed. An
    // override saved before a later step is refused counts as saved, so a
    // second try sends only what is left.
    mutationFn: async (sent: PolicyValues) => {
      let org;
      for (const name of changedOverrides(sent, form.getInitialValues())) {
        org = (await api.org.setFeature({ orgId, feature: OVERRIDE_FEATURE[name], enabled: sent.overrides[name] })).org;
        const saved = form.getInitialValues();
        form.setInitialValues({ ...saved, overrides: { ...saved.overrides, [name]: sent.overrides[name] } });
      }
      if (!samePolicy(sent, form.getInitialValues())) org = (await api.org.setPolicy({ orgId, policy: toPolicy(sent) })).org;
      return org;
    },
    // What the server stored is the saved state: it may have adjusted what
    // was sent. The form takes it too unless it was edited during the save.
    onSuccess: (org, sent) => {
      const stored = org?.policy ? toValues(org.policy, org.features) : sent;
      const editedMeanwhile = !sameValues(form.getValues(), sent);
      form.setInitialValues(stored);
      if (!editedMeanwhile) form.setValues(stored);
      void inv('org');
      notifications.show({ message: 'Policy saved. It applies at each operator\'s next sign-in or refresh.' });
    },
    onError: (e) => { void inv('org'); notifications.show({ message: errorText(e), color: 'danger' }); },
  });
  const v = form.values;
  const pin = v.offlineResume === OfflineResume.PIN;
  // The timeline follows the form, drawn from the nearest values it accepts
  // while a field is out of range, and not at all while one is empty.
  const within = (n: number, l: { min: number; max: number }) => Math.min(Math.max(Math.round(n), l.min), l.max);
  // A full sign-in asks for MotionSafe's legal documents while any is in
  // effect and no custom agreement covers the organization.
  const documents = useQuery(legalDocumentsQuery());
  const agreement = useQuery(orgAgreementQuery(orgId));
  const legalAsked = !!documents.data?.some((d) => d.status === 'active' && d.inEffect) && !!agreement.data && !agreement.data.current;
  const timeline = typeof v.sessionTtlHours === 'number' && typeof v.offlineGraceHours === 'number' && typeof v.maxSignInDays === 'number'
    ? (() => {
        const sessionHours = within(v.sessionTtlHours, LIMITS.sessionTtlHours);
        return { sessionHours, graceHours: within(v.offlineGraceHours, LIMITS.offlineGraceHours), pin, signInDays: Math.max(within(v.maxSignInDays, LIMITS.maxSignInDays), Math.ceil(sessionHours / 24)), idleMinutes: pin && v.idleLock && typeof v.idleLockMinutes === 'number' ? Math.min(within(v.idleLockMinutes, LIMITS.idleLockMinutes), sessionHours * 60) : 0, legal: legalAsked };
      })()
    : null;
  const dirty = !sameValues(v, form.getInitialValues());
  const invalid = !form.isValid();
  const mfaLocked = v.mfaLocked && !staff;
  const setRequireMfa = (on: boolean) => form.setFieldValue('requireMfa', on);
  // A sharing setting: locked and off, naming who can change it, while its
  // override does not allow it; staff keep the real control, told that it
  // has no effect while the override above it says no.
  const sharingSwitch = (field: SharingField) => {
    const { label, hint, override, missing } = SHARING[field];
    const allowed = v.overrides[override];
    const description = allowed ? hint : lockedNote(hint, missing.staff);
    if (!allowed && !staff) return <LockedSwitch label={label} checked={false} description={lockedNote(hint, missing.theirs)} />;
    if (!write) return <LockedSwitch label={label} checked={allowed && v[field]} aria-describedby={saveCaption} description={description} />;
    return <Switch label={label} description={description} {...form.getInputProps(field, { type: 'checkbox' })} />;
  };
  // A sharing override, above the settings it holds; staff only.
  const overrideSwitch = (name: Override) => {
    const { label } = OVERRIDE[name];
    // Transfers between organizations ride Wi-Fi Direct.
    const hint = name === 'crossOrg' && !v.overrides.wifiDirect ? `${OVERRIDE[name].hint} Needs Wi-Fi Direct, which MotionSafe does not allow for this organization.` : OVERRIDE[name].hint;
    if (!writeOverrides) return <LockedSwitch label={label} checked={v.overrides[name]} description={lockedNote(hint, overrideCaption)} />;
    return <Switch label={label} description={hint} {...form.getInputProps(`overrides.${name}`, { type: 'checkbox' })} />;
  };

  return (
    <>
      <PageHeader title={TITLE} description={PURPOSE} />
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
        {/* First, and across the page: every setting below feeds it. */}
        {timeline && (
          <Section wide title="How sign-in works" description="One sign-in from start to finish, from the settings below as they stand. Pick what happens to the device.">
            <SignInTimeline {...timeline} />
          </Section>
        )}
        <Section title="Sessions" description="How long a device goes between checks that its operator is still the one holding it.">
          <Amount label="Session length" description={pin ? 'How long the app goes before it asks for the operator\'s PIN again. A device that cannot reach MotionSafe keeps working this long from its last check-in, plus the renewal grace period.' : 'How long a sign-in lasts before the operator signs in again. A device that cannot reach MotionSafe keeps working this long from its last check-in, plus the renewal grace period.'}
            {...LIMITS.sessionTtlHours} unit="hours" locked={!write} {...form.getInputProps('sessionTtlHours')} />
          <Divider />
          <Amount label="Renewal grace period" description="How much longer a device keeps working past its session length when it cannot reach MotionSafe: time to reach a connection and renew. Still offline after it, the device signs its operator out."
            {...LIMITS.offlineGraceHours} unit="hours" locked={!write} {...form.getInputProps('offlineGraceHours')} />
          <Divider />
          <Amount label="Full sign-in every" description={pin
            ? 'How often an operator signs in again with their password and 2FA. In between, the app asks for their PIN at the end of each session length.'
            : 'Applies with PIN locking on. Without it, an operator signs in again with their password at the end of each session length.'}
            {...LIMITS.maxSignInDays} unit="days" locked={!write} disabled={write && !pin} {...form.getInputProps('maxSignInDays')} />
        </Section>

        <Section title="Locking" description="Stepping away from a device without signing out.">
          <Switch label="Lock with a PIN" description="Each operator chooses a PIN for the app when they sign in, unlocks with it, and confirms with it at the end of each session length. Changing it signs them out. Off: they sign in again with their password instead."
            checked={v.offlineResume === OfflineResume.PIN}
            {...(write
              ? { onChange: (e: ChangeEvent<HTMLInputElement>) => form.setFieldValue('offlineResume', e.currentTarget.checked ? OfflineResume.PIN : OfflineResume.DISABLED) }
              : lockedSwitch(saveCaption))} />
          <Divider />
          <Stack gap="md">
            <Switch label="Lock after inactivity" description={pin
              ? 'The app locks and asks for the PIN when nobody has touched it for this long, or when it comes back after being put away that long. Work already running is never cut off.'
              : 'Applies with PIN locking on.'}
              checked={pin && v.idleLock}
              {...(write
                ? { disabled: !pin, onChange: (e: ChangeEvent<HTMLInputElement>) => form.setFieldValue('idleLock', e.currentTarget.checked) }
                : lockedSwitch(saveCaption))} />
            {/* Under the switch's own text: the switch's track and the gap beside it. */}
            <Box pl={SWITCH_INDENT}>
              <Amount label="Lock after" {...LIMITS.idleLockMinutes} unit="minutes" locked={!write} disabled={write && !(pin && v.idleLock)} {...form.getInputProps('idleLockMinutes')} />
            </Box>
          </Stack>
          <Divider />
          <Amount label="Locked operators per device" description={pin ? 'From 1 to 5. One more pushes out whoever has been locked longest.' : 'Applies with PIN locking on. Without it, an operator signs out to hand a device over, and nobody is kept locked on it.'}
            {...LIMITS.maxCachedSessions} locked={!write} disabled={write && !pin} {...form.getInputProps('maxCachedSessions')} />
        </Section>

        <Section title="2FA" description="A code from an authenticator app, entered after the password at sign-in.">
          {mfaLocked ? (
            <LockedSwitch label="Require 2FA" checked={v.requireMfa}
              description="Required by MotionSafe under your agreement. Contact your account manager to change it." />
          ) : !write ? (
            <LockedSwitch label="Require 2FA" checked={v.requireMfa} aria-describedby={saveCaption}
              description="Applies from each person's next sign-in." />
          ) : (
            <Switch label="Require 2FA" description="Applies from each person's next sign-in."
              checked={v.requireMfa} onChange={(e) => setRequireMfa(e.currentTarget.checked)} />
          )}
          {staff && (
            <>
              <Divider />
              <Switch label="Lock the requirement" description="Staff only. The organization's administrators cannot change the switch above."
                {...form.getInputProps('mfaLocked', { type: 'checkbox' })} {...(write ? {} : lockedSwitch(saveCaption))} />
            </>
          )}
        </Section>

        {/* The organization's own sharing settings, each under MotionSafe's override, which only staff see. */}
        <Section title="Data sharing" description="What operators may hand over from a device, beyond the encrypted collection.">
          {staff && <>{overrideSwitch('decryptedSharing')}<Divider /></>}
          {sharingSwitch('decryptedSharing')}
        </Section>

        <Section title="Wi-Fi Direct transfer" description="Sending a collection from one device to another, within this organization and beyond it.">
          {staff && <>{overrideSwitch('wifiDirect')}<Divider /></>}
          {sharingSwitch('wifiDirectSend')}
          <Divider />
          {sharingSwitch('wifiDirectReceive')}
          <Divider />
          {staff && <>{overrideSwitch('crossOrg')}<Divider /></>}
          {sharingSwitch('crossOrgSend')}
          <Divider />
          {sharingSwitch('crossOrgReceive')}
        </Section>

      </SimpleGrid>

      <Group
        justify="space-between"
        gap="md"
        wrap="nowrap"
        pos="sticky"
        bottom={0}
        mt="lg"
        py="md"
        style={{ zIndex: 2, background: 'var(--mantine-color-body)', borderTop: '1px solid var(--mantine-color-default-border)' }}
      >
        {write || (writeOverrides && dirty)
          ? <Text size="xs" c="dimmed" role="status">{dirty ? (invalid ? 'Unsaved changes. Correct the settings noted above to save.' : 'Unsaved changes') : ''}</Text>
          : null}
        {/* The policy's own caption, which its locked settings point to, stays while overrides are being changed. */}
        {!write && <Text size="xs" c="dimmed" id={saveCaption} hidden={writeOverrides && dirty}>{access.caption('SetPolicy')}</Text>}
        <Group gap="sm" wrap="nowrap">
          {dirty && <Button variant="default" onClick={() => form.reset()} disabled={save.isPending}>Discard</Button>}
          {write || writeOverrides
            ? <Button onClick={() => { if (!invalid) save.mutate(form.getValues()); }} disabled={!dirty || invalid || save.isPending}>Save</Button>
            : <Button variant="default" {...locked(saveCaption)}>Save</Button>}
        </Group>
      </Group>
    </>
  );
}
